import { format, parse } from 'date-fns'
import { JobCriteriaForm, SalaryType, SingleStepJobForm, TypedSalary } from '../../../schema/job'
import { ApplyViaType } from '../../../stories/organisms/CompanyForm/CompanyContact/CompanyContact'
import { FormAsValues, UseFormType } from '../../../util/useForm/useForm'
import {
  type ApplyVia,
  type ApplyViaExternal,
  type ApplyViaJobBoard,
  type classifiedFunctionGroup,
  type DownStreamSalaryType,
  type Draft,
  type FunctionGroupType,
  type unclassifiedFunctionGroup,
} from '../../../state/reducers/draft/draft'
import type { DashboardUser, Company, CompanyType, CorporateIdentityTemplate } from '../../../schema/user'
import { OptionOrString } from '../../../stories/atoms/SelectOrFreeTextMenu/SelectOrFreeTextMenu'
import { COMPANY_TYPE_HUMAN_RESOURCE_MANAGER, COMPANY_TYPE_MEDIABUREAU } from '../../../state/constants'

const NO_TEMPLATE = '- (Geen huisstijl)'
const DATE_FORMAT = 'dd-MM-yyyy'
const COUNTRY = 'NL'

export const MIN_SALARY = 0
export const MAX_SALARY = 100_000_000_000 // Set it to a high number to allow for any salary

export const MIN_HOURLY_SALARY: number = 0
export const MIN_MONTHLY_SALARY: number = 1000
export const MIN_YEARLY_SALARY: number = 20001
const salaryTypeRangeList = [
  { type: SalaryType.PerHour, min: MIN_HOURLY_SALARY, max: MIN_MONTHLY_SALARY - 1 },
  { type: SalaryType.PerMonth, min: MIN_MONTHLY_SALARY, max: MIN_YEARLY_SALARY - 1 },
  { type: SalaryType.PerYear, min: MIN_YEARLY_SALARY, max: Infinity },
]

export const calculateSalaryTypeByAmount = (min: number, max: number): SalaryType => {
  let longestOverlap = -1
  let currentSalaryType = SalaryType.PerMonth

  if (min < max / 2) {
    // The range is too big. Probably not what the recruiter intended.
    // Let's calculate the type based on max only.
    if (max < MIN_MONTHLY_SALARY) {
      return SalaryType.PerHour
    }

    if (max < MIN_YEARLY_SALARY) {
      return SalaryType.PerMonth
    }

    return SalaryType.PerYear
  }

  salaryTypeRangeList.forEach(({ type: salaryType, min: rangeMin, max: rangeMax }) => {
    const overlapStart = Math.max(rangeMin, min)
    const overlapEnd = Math.min(rangeMax, max)
    const overlapLength = overlapEnd - overlapStart

    if (overlapLength > longestOverlap) {
      longestOverlap = overlapLength
      currentSalaryType = salaryType
    }
  })

  return currentSalaryType
}

export const mapDraftApplyViaToFormApplyVia = (applyVia: 'ByExternalUrl' | 'ByEmail' | 'ByEasyApply') => {
  if (applyVia === 'ByEmail') {
    return ApplyViaType.Email
  }
  if (applyVia === 'ByEasyApply') {
    return ApplyViaType.EasyApply
  }
  return ApplyViaType.Website
}

export const canTypeCompanyNameList: CompanyType[] = [COMPANY_TYPE_MEDIABUREAU, COMPANY_TYPE_HUMAN_RESOURCE_MANAGER]

// Some companies can set company name with a text input instead of a select input
export const canTypeCompanyName = (companyType: CompanyType) => canTypeCompanyNameList.includes(companyType)

const mapCorporateIdentityTemplateIdToName = (
  templateId: string,
  templates: Company['corporateIdentityTemplates'] = [],
) => {
  const templatesWithDefault = [
    { id: templateId === undefined ? templateId : 'none', name: NO_TEMPLATE, isDefault: false },
    ...templates,
  ]

  const sortedTemplates = templatesWithDefault.sort(entry => {
    if (entry.isDefault) return -1
    return 0
  })

  return sortedTemplates.find(({ id }) => templateId === id)?.name
}

const mapCorporateIdentityTemplateNameToId = (
  templateName: string,
  templates: Company['corporateIdentityTemplates'] = [],
) => {
  const templatesWithDefault = [{ id: 'none', name: NO_TEMPLATE, isDefault: false }, ...templates]
  return templatesWithDefault.find(({ name }) => templateName === name)?.id
}

export const getCorporateIdentityTemplateOptions = (templates: CorporateIdentityTemplate[] = []) => [
  NO_TEMPLATE,
  ...templates.map(({ name }) => name),
]

export const mapFunctionGroupToDraft = (value: OptionOrString): FunctionGroupType => {
  if (value.userDefinedOption) {
    return {
      unclassified: value.userDefinedOption,
    }
  } else {
    return {
      classified: {
        value: value.selectedOption?.value,
        label: value.selectedOption?.label,
      },
    }
  }
}

const isClassified = (value: FunctionGroupType): value is classifiedFunctionGroup => {
  return (value as classifiedFunctionGroup)?.classified !== undefined
}

const isUnClassified = (value: FunctionGroupType): value is unclassifiedFunctionGroup => {
  return (value as unclassifiedFunctionGroup)?.unclassified !== undefined
}

export const mapFunctionGroupToForm = (value: FunctionGroupType): OptionOrString => {
  if (isClassified(value)) {
    return {
      userDefinedOption: '',
      selectedOption: {
        label: value.classified.label,
        value: value.classified.value,
      },
    }
  } else if (isUnClassified(value)) {
    return {
      userDefinedOption: value.unclassified,
      selectedOption: {
        label: '',
        value: '',
      },
    }
  } else {
    return {
      userDefinedOption: '',
      selectedOption: {
        label: '',
        value: '',
      },
    }
  }
}

const getApply = (formValues: FormAsValues<SingleStepJobForm>): ApplyVia => {
  switch (formValues.applyVia) {
    case ApplyViaType.Email:
      return {
        applyOption: 'ByEmail',
        via: 'jobboard',
        firstName: formValues.applyViaFirstName,
        lastName: formValues.applyViaLastName,
        emailAddress: formValues.applyViaEmail,
      }
    case ApplyViaType.Website:
      return {
        applyOption: 'ByExternalUrl',
        via: 'external',
        url: formValues.applyViaWebsite,
      }
  }

  // Assumed ApplyViaType.EasyApply
  return {
    applyOption: 'ByEasyApply',
    via: 'easyapply',
  }
}

export const determineDomainSalaryType = (salary: DownStreamSalaryType): SalaryType => {
  switch (salary.type) {
    case 'unspecified':
      return calculateSalaryTypeByAmount(salary.lower, salary.upper)
    case 'hourly':
      return SalaryType.PerHour
    case 'yearly':
      return SalaryType.PerYear
    case 'monthly':
    default:
      return SalaryType.PerMonth
  }
}

const mapSalaryTypes = new Map<SalaryType, DownStreamSalaryType['type']>([
  [SalaryType.PerHour, 'hourly'],
  [SalaryType.PerMonth, 'monthly'],
  [SalaryType.PerYear, 'yearly'],
  [SalaryType.NoType, 'unspecified'],
])

export const mapFormSalaryToDraft = (
  flag: boolean,
  salary: JobCriteriaForm['salary'],
  typedSalary: JobCriteriaForm['typedSalary'],
): DownStreamSalaryType | undefined => {
  if (flag) {
    if (typedSalary?.min === undefined && typedSalary?.max === undefined) {
      return undefined
    } else {
      return {
        lower: typedSalary.min,
        upper: typedSalary.max,
        type: mapSalaryTypes.get(typedSalary.type),
      }
    }
  } else {
    if (!salary?.min && !salary?.max) {
      return undefined
    } else {
      return {
        lower: Number(salary?.min),
        upper: Number(salary?.max),
        // We need to provide a salary type for the GRPC to use the correct (new) salary type, which supports floats,
        // instead of the "SalaryRange" type, which only works with integers.
        // We use the "NoType" salary type, as when the feature flag is disabled, the user cannot provide a salary type.
        type: mapSalaryTypes.get(SalaryType.NoType),
      }
    }
  }
}

export const mapValidFormToDraft = ({
  formValues,
  draft,
  user,
  isFeatureFlagSalaryTypeEnabled = false,
}: {
  formValues: FormAsValues<SingleStepJobForm>
  draft: Draft
  user: DashboardUser
  isFeatureFlagSalaryTypeEnabled?: boolean
}): Draft => ({
  content: {
    title: formValues.title,
    description: formValues.jobText,
    functionGroup: mapFunctionGroupToDraft(formValues.functionGroup),
    jobCategories: formValues.functionGroups,
    industryCategories: formValues.branches,
  },
  criteria: {
    educationLevels: formValues.educationLevels,
    careerLevels: [formValues.experienceLevel],
  },
  contract: {
    contractTypes: formValues.contractTypes,
    workingHours: {
      lower: Number(formValues.hours?.min),
      upper: Number(formValues.hours?.max),
    },
    remoteWork: formValues.remoteWork,
    salary: mapFormSalaryToDraft(isFeatureFlagSalaryTypeEnabled, formValues.salary, formValues.typedSalary),
  },
  publicationPeriod: {
    start: formValues.publicationDate.startDate
      ? parse(formValues.publicationDate.startDate, DATE_FORMAT, new Date())
      : undefined,
    end: formValues.publicationDate.endDate
      ? parse(formValues.publicationDate.endDate, DATE_FORMAT, new Date())
      : undefined,
  },
  location: {
    country: COUNTRY,
    postalCode: formValues.place?.value,
  },
  applyVia: getApply(formValues),
  company: {
    name: formValues.company,
    companyType: draft.company.companyType,
    availableNames: draft.company.availableNames,
    huisstijl: mapCorporateIdentityTemplateNameToId(
      formValues.corporateIdentityTemplate || '',
      user.company?.corporateIdentityTemplates,
    ),
    website: draft.company.website,
  },
  contactInformation: {
    contact: {
      name: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
      },
      emailAddress: formValues.email,
      phoneNumber: formValues.phoneNumber,
    },
    address: {
      streetNameAndHouseNumber: formValues.address,
      zipCode: formValues.zipCode,
      city: formValues.city,
    },
    website: formValues.website,
  },
  logo: formValues.logo
    ? {
        key: formValues.logo,
      }
    : undefined,
  video: {
    url: formValues.videoUrl,
  },
})

const mapSalary = (salary?: DownStreamSalaryType): TypedSalary => {
  if (!salary) {
    return {
      min: undefined,
      max: undefined,
      type: SalaryType.PerMonth,
    }
  } else {
    return {
      min: salary.lower ?? Number(salary.lower),
      max: salary.upper ?? Number(salary.upper),
      type: determineDomainSalaryType(salary),
    }
  }
}

export const mapDraftToForm = ({
  draft,
  company,
  initialValues,
}: {
  draft: Draft
  company: Company
  initialValues: SingleStepJobForm
}): SingleStepJobForm => {
  const {
    content,
    contract,
    location,
    criteria,
    applyVia,
    company: draftCompany,
    publicationPeriod,
    video,
    contactInformation,
    logo,
  } = draft
  const defaultCorporateIdentityTemplateId = company.corporateIdentityTemplates?.find(({ isDefault }) => isDefault)?.id

  return {
    ...initialValues,
    title: content.title,
    functionGroups: content.jobCategories,
    branches: content.industryCategories,
    functionGroup: mapFunctionGroupToForm(content.functionGroup),
    hours: {
      min: contract.workingHours?.lower ? String(contract.workingHours?.lower) : '',
      max: contract.workingHours?.upper ? String(contract.workingHours?.upper) : '',
    },
    salary: {
      min: contract.salary?.lower ? String(contract.salary?.lower) : '',
      max: contract.salary?.upper ? String(contract.salary?.upper) : '',
    },
    typedSalary: mapSalary(contract.salary),
    place: {
      value: location.postalCode,
      label: location.postalCode,
    },
    educationLevels: criteria.educationLevels,
    experienceLevel: criteria.careerLevels[0],
    contractTypes: contract.contractTypes,

    // vacature text
    corporateIdentityTemplate: mapCorporateIdentityTemplateIdToName(
      draftCompany.huisstijl || defaultCorporateIdentityTemplateId || '',
      company.corporateIdentityTemplates,
    ),
    jobText: content.description,
    videoUrl: video?.url,

    // company form
    address: contactInformation?.address?.streetNameAndHouseNumber || company.postalAddress?.streetNameAndHouseNumber,
    city: contactInformation?.address?.city || company.postalAddress?.city,
    company: draftCompany.name,
    email: contactInformation?.contact.emailAddress,
    firstName: contactInformation?.contact.name.firstName,
    lastName: contactInformation?.contact.name.lastName,
    logo: logo?.key,
    phoneNumber: contactInformation?.contact.phoneNumber,
    website: contactInformation?.website,
    zipCode: contactInformation?.address?.zipCode || company.postalAddress?.zipCode,

    // company form (applyVia)
    applyVia: mapDraftApplyViaToFormApplyVia(applyVia.applyOption),
    applyViaFirstName: (applyVia as ApplyViaJobBoard).firstName,
    applyViaLastName: (applyVia as ApplyViaJobBoard).lastName,
    applyViaEmail: (applyVia as ApplyViaJobBoard).emailAddress,
    applyViaWebsite: (applyVia as ApplyViaExternal).url,

    // publication form
    publicationDate: {
      startDate: format(new Date(publicationPeriod.start), DATE_FORMAT),
      endDate: format(new Date(publicationPeriod.end), DATE_FORMAT),
    },
    remoteWork: contract.remoteWork,
  }
}
